import { message } from 'antd';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import {
  Form,
  FormItemInputNumber,
  GenericDrawerHeader,
  Title,
} from '../../../../components/index';
import { useUpdateOperationMutation } from '../../../../services/operationApi';
import {
  OperationalOperationToSave,
  OperationalOperationType,
  ProvisionedCargoType,
  Stopover,
  SystemParamItemType,
} from '../../../../types';
import {
  formatOperationToSave,
  formatOperationToForm,
} from '../../../../utils/formatters';
import { tosApi } from '../../../../services/tosApi';
import { ProvisionedCargoMovimentationHasMissingAttributes } from '../../../../utils/validators';

type ProvisionedCargoMovimentationProps = {
  onCloseDrawer?: (show: boolean) => void;
  operation: OperationalOperationType;
  selectedStopover?: Stopover;
};

export function ProvisionedCargoMovimentation(
  props: ProvisionedCargoMovimentationProps
) {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { onCloseDrawer, operation, selectedStopover } = props;

  const maxTolerancePercent = Number(
    process.env.REACT_APP_MAX_OPERATION_TOLERANCE_PERCENT || 20
  );
  const toleranceFactor = 1 + maxTolerancePercent / 100;

  function closeDrawer() {
    if (onCloseDrawer) {
      onCloseDrawer(false);
    }
  }

  const [
    updateOperation,
    {
      isLoading: isLoadingUpdateOperation,
      isSuccess: isSuccessUpdateOperation,
      data: operationUpdatedData,
    },
  ] = useUpdateOperationMutation();

  const totalOperated = useMemo(() => {
    const total = operation?.provisioned_cargos?.reduce((acc, cargo) => {
      return acc + (cargo.total_movimented || 0);
    }, 0);

    if (total && total > 0) {
      return `Total operado: ${total} ton`;
    }
    return 'Não operado';
  }, [operation?.provisioned_cargos]);

  function getHeader() {
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <strong style={{ fontSize: '16px' }}>Registrar movimentação</strong>
        <span style={{ fontWeight: '400', color: 'var(--neutral_medium)' }}>
          {totalOperated}
        </span>
      </div>
    );
  }

  function handleUpdateMovimented() {
    form
      .validateFields()
      .then(async (values) => {
        const operationToSave: OperationalOperationToSave =
          formatOperationToSave(operation);

        try {
          const missingAttributes =
            ProvisionedCargoMovimentationHasMissingAttributes(
              operation.provisioned_cargos
            );

          if (missingAttributes) {
            return;
          }

          operationToSave.provisioned_cargos = operation.provisioned_cargos.map(
            (cargo, index) => ({
              ...cargo,
              supplier_company: cargo.supplier_company.id,
              supply_type: cargo.supply_type.name,
              total_weight: cargo.total_weight,
              operation_type: cargo.operation_type
                ? cargo.operation_type.id
                : null,
              navigation_type: cargo.navigation_type
                ? cargo.navigation_type?.id
                : null,
              operation_direction: cargo.operation_direction
                ? cargo.operation_direction
                : null,
              loading_port: cargo.loading_port
                ? cargo.loading_port.bi_trigram
                : null,
              unloading_port: cargo.unloading_port
                ? cargo.unloading_port.bi_trigram
                : null,
              ncm_code: cargo.ncm_code ? cargo.ncm_code : null,
              amount: cargo.amount ? cargo.amount : null,
              imdg_code: cargo.imdg_code ? cargo.imdg_code?.id : null,
              storage_company: cargo.storage_company
                ? cargo.storage_company?.id
                : null,
              consignee_company: cargo.consignee_company
                ? cargo.consignee_company?.id
                : null,
              total_movimented:
                values.provisioned_cargos[index].total_movimented,
            })
          );

          await updateOperation(operationToSave).unwrap();
          message.success('Movimentação atualizada com sucesso!', 5);
          dispatch(
            tosApi.util.invalidateTags([
              { type: 'OperationCalculus' },
              { type: 'Stopover' },
            ])
          );

          closeDrawer();
        } catch (err) {
          message.error(`Erro ao atualizar operação: ${err}`, 5);
        }
      })
      .catch((error) => {
        console.error('Erro na validação dos campos', error);
      });
  }

  return (
    <Form
      form={form}
      name="movimentation"
      initialValues={formatOperationToForm(operation)}
      onFinish={handleUpdateMovimented}
    >
      <GenericDrawerHeader
        title={getHeader()}
        formName="movimentation"
        tabs={[]}
        showBackButton
        onBack={closeDrawer}
        isLoadingSave={isLoadingUpdateOperation}
      />

      <div style={{ padding: '24px 16px' }}>
        {operation?.provisioned_cargos?.map(
          (provisionedCargo: ProvisionedCargoType, index: number) => (
            <div
              key={index}
              style={{
                borderBottom: '1px solid var(--neutral_lighter)',
                marginBottom: '20px',
              }}
            >
              <Title>
                NCM {provisionedCargo.ncm_code} -{' '}
                {provisionedCargo.supply_type?.name}
                {(() => {
                  try {
                    const terminalName = provisionedCargo.storage_company?.name;
                    const provisionedWeight = provisionedCargo.total_weight;

                    let result = '';
                    if (terminalName) {
                      result += ` - Terminal de Armazenamento: ${terminalName}`;
                    }
                    if (provisionedWeight) {
                      result += ` - Peso Provisionado: ${provisionedWeight.toLocaleString(
                        'pt-BR'
                      )} ton`;
                    }
                    return result;
                  } catch (error) {
                    return '';
                  }
                })()}
              </Title>
              <FormItemInputNumber
                colSpan={12}
                name={['provisioned_cargos', index, 'total_movimented']}
                label="Peso movimentado"
                addonAfter="ton"
                rules={[
                  {
                    validator: (_, value) => {
                      if (
                        value &&
                        provisionedCargo &&
                        provisionedCargo.total_weight
                      ) {
                        const maxAllowed =
                          provisionedCargo.total_weight * toleranceFactor;
                        if (value > maxAllowed) {
                          return Promise.reject(
                            new Error(
                              `Excedeu o limite de ${maxTolerancePercent}% (máx: ${maxAllowed.toLocaleString(
                                'pt-BR'
                              )} ton)`
                            )
                          );
                        }
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </div>
          )
        )}
      </div>
    </Form>
  );
}
